import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { IntroductionComponent } from './introduction.component';
import { Step1Component } from './step-1.component';
import { Step2Component } from './step-2.component';
import { Step3Component } from './step-3.component';
import { FinalComponent } from './final.component';
import { VisualBuilderComponent } from './visual-builder.component';
import { AssigningComponent } from './assigning.component';
import { OnboardingTableChartComponent } from './table-chart.component';
import { ThemeExtensionComponent } from './theme-extension.component';
import { TableChart } from 'src/app/types/table-chart';
import { ChartComponent } from './chart.component';
import { Chart } from 'src/app/types/chart';
import { ChartService } from 'src/app/services/chart/chart.service';
import { captureException } from '@sentry/angular';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService, _ } from '@ngx-translate/core';
import { LayoutService } from 'src/app/services/layout/layout.service';
import { CTAButton } from 'src/app/types/general';

class ProceedWithoutProductIdError extends Error {}

@Component({
  templateUrl: './onboarding.component.html',
  standalone: true,
  selector: 'app-onboarding',
  imports: [
    IntroductionComponent,
    Step1Component,
    Step2Component,
    Step3Component,
    FinalComponent,
    VisualBuilderComponent,
    AssigningComponent,
    OnboardingTableChartComponent,
    ThemeExtensionComponent,
    ChartComponent,
  ],
})
export class OnboardingComponent implements OnInit, OnDestroy {
  private _lastCtaButton: CTAButton | undefined;

  private readonly layoutService = inject(LayoutService);
  private readonly chartService = inject(ChartService);
  private readonly snackbar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  readonly step = signal(0);
  readonly themeExtensionFrom = signal(5);
  readonly tableChart = signal<TableChart | undefined>(undefined);
  readonly chart = signal<Chart | undefined>(undefined);
  readonly productId = signal<string | undefined>(undefined);

  ngOnInit(): void {
    this._lastCtaButton = this.layoutService.popCtaButton();
  }

  goToThemeExtension(from: number) {
    this.themeExtensionFrom.set(from);
    this.step.set(9);
  }

  onStep3Continue(nextStep: number) {
    if (nextStep === 9) this.goToThemeExtension(5);
    else this.step.set(nextStep);
  }

  ngOnDestroy(): void {
    this.layoutService.ctaButton = this._lastCtaButton;
  }

  onStep2Continue(event: {
    nextStep: number;
    payload: { chart: TableChart | Chart; productId: string | undefined } | undefined;
  }) {
    try {
      if (event.nextStep === 8 && !event?.payload?.productId) throw new ProceedWithoutProductIdError();

      this.tableChart.set(undefined);
      this.chart.set(undefined);
      this.productId.set(event?.payload?.productId);
      this.chartService.chart = event?.payload?.chart;

      if (event.payload) {
        if (event.nextStep === 4) this.tableChart.set(event.payload.chart as TableChart);
        if (event.nextStep === 8) this.chart.set(event.payload.chart as Chart);
      }

      this.step.set(event.nextStep);
    } catch (error) {
      captureException(error);

      this.snackbar.open(
        this.translate.instant(
          _(
            error instanceof ProceedWithoutProductIdError
              ? 'onboarding.failed-to-proceed-please-try-again'
              : 'error.error-occured',
          ),
        ),
        this.translate.instant(_('common.close')),
        {
          panelClass: ['snackbar-error'],
        },
      );

      this.step.set(2);
    }
  }
}
